// src/features/downloads/components/HeroInstallCta.tsx
//
// The one primary action on the site. Clarity showed visitors clicking
// Windows / macOS / NPM ten times more often than "Get Started Free", so the
// download IS the funnel entry: detect the visitor's OS, offer that build in
// one big button, keep the other platforms and npm one click away.
//
// Every click is tagged so the download → app-sign-in → first-message funnel
// can finally be measured (download_click:<os>, npm_copy, cta_click:*).

import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { ContentCopy as ContentCopyIcon, Check as CheckIcon, Download as DownloadIcon } from '@mui/icons-material';
import { brand } from '../../../config/brand';
import { buildArtifactUrls, type BrandId } from '../../../config/releases';
import { useReleases } from '../hooks/useReleases';
import { detectPlatform, type Platform } from '../utils/platformDetection';
import clarityService from '../../../services/clarityService';

type ArtifactKey = 'windows' | 'mac' | 'linux';

const PLATFORM_TO_ARTIFACT: Record<Platform, ArtifactKey | null> = {
  windows: 'windows',
  macos: 'mac',
  linux: 'linux',
  unknown: null,
};

const LABEL: Record<ArtifactKey, string> = { windows: 'Windows', mac: 'macOS', linux: 'Linux' };
const REQUIREMENT: Record<ArtifactKey, string> = {
  windows: 'Windows 10 or later',
  mac: 'Apple Silicon',
  linux: 'Debian / Ubuntu (.deb)',
};

interface HeroInstallCtaProps {
  /** `onDark` = white controls on the hero gradient; `onLight` = themed page section. */
  tone?: 'onDark' | 'onLight';
  /** Compact = primary button + platform links only (used in the closing CTA). */
  compact?: boolean;
  /** Where on the page this instance lives — recorded with every click. */
  placement?: string;
}

const HeroInstallCta: React.FC<HeroInstallCtaProps> = ({ tone = 'onDark', compact = false, placement = 'hero' }) => {
  const { loading, stable, experimental } = useReleases();
  const brandId: BrandId = (brand.id as BrandId) || 'autopilot';
  const detected = useMemo(() => detectPlatform(), []);
  const primaryKey = PLATFORM_TO_ARTIFACT[detected.platform];
  const [copied, setCopied] = useState(false);

  const release = stable || experimental;
  const artifacts = useMemo(
    () => (release ? buildArtifactUrls(release.version, brandId) : null),
    [release, brandId],
  );

  const dark = tone === 'onDark';
  const textColor = dark ? 'white' : 'text.primary';
  const mutedColor = dark ? 'rgba(255,255,255,0.75)' : 'text.secondary';

  const trackDownload = (key: ArtifactKey, kind: 'primary' | 'secondary') => {
    clarityService.track('download_click', `${key}_${kind}_${placement}`);
  };

  const copyNpm = () => {
    const cmd = artifacts?.npm.installCommand || brand.installCommand;
    navigator.clipboard?.writeText(cmd).catch(() => {});
    setCopied(true);
    clarityService.track('npm_copy', placement);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 520 }}>
        <Skeleton variant="rounded" height={56} sx={{ bgcolor: dark ? 'rgba(255,255,255,0.15)' : undefined, mb: 1 }} />
        <Skeleton variant="text" width={280} sx={{ bgcolor: dark ? 'rgba(255,255,255,0.15)' : undefined }} />
      </Box>
    );
  }

  // No release list available (blob unreachable): fall back to npm, which
  // always works, rather than rendering nothing.
  if (!artifacts || !release) {
    return (
      <Box sx={{ maxWidth: 520 }}>
        <NpmRow command={brand.installCommand} copied={copied} onCopy={copyNpm} dark={dark} />
      </Box>
    );
  }

  const otherKeys = (['windows', 'mac', 'linux'] as ArtifactKey[]).filter((k) => k !== primaryKey);

  return (
    <Box sx={{ maxWidth: 560 }}>
      {primaryKey ? (
        <Button
          variant="contained"
          size="large"
          href={artifacts[primaryKey].url}
          onClick={() => trackDownload(primaryKey, 'primary')}
          startIcon={<DownloadIcon />}
          sx={{
            px: 4,
            py: 1.6,
            fontSize: '1.1rem',
            fontWeight: 800,
            textTransform: 'none',
            borderRadius: 2,
            background: brand.gradients.button,
            color: brand.buttonTextColor,
            boxShadow: `0 10px 30px ${brand.gradients.glow}`,
            '&:hover': { background: brand.gradients.buttonHover },
          }}
        >
          Download for {LABEL[primaryKey]}
        </Button>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {(['windows', 'mac', 'linux'] as ArtifactKey[]).map((key) => (
            <Button
              key={key}
              variant="contained"
              size="large"
              href={artifacts[key].url}
              onClick={() => trackDownload(key, 'primary')}
              startIcon={<DownloadIcon />}
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                background: brand.gradients.button,
                color: brand.buttonTextColor,
                '&:hover': { background: brand.gradients.buttonHover },
              }}
            >
              {LABEL[key]}
            </Button>
          ))}
        </Stack>
      )}

      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: mutedColor }}>
        Free · v{release.version}
        {primaryKey ? ` · ${REQUIREMENT[primaryKey]}` : ' · runs on your computer — open this page there to install'}
        {' · '}
        <Box
          component="a"
          href={artifacts.github.releasePageUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'inherit', textDecoration: 'underline' }}
        >
          release notes
        </Box>
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
        {primaryKey && (
          <Typography variant="caption" sx={{ color: mutedColor }}>
            Also for
          </Typography>
        )}
        {primaryKey && otherKeys.map((key) => (
          <Chip
            key={key}
            component="a"
            clickable
            href={artifacts[key].url}
            onClick={() => trackDownload(key, 'secondary')}
            label={LABEL[key]}
            size="small"
            variant="outlined"
            sx={{
              color: textColor,
              borderColor: dark ? 'rgba(255,255,255,0.4)' : 'divider',
              '&:hover': { bgcolor: dark ? 'rgba(255,255,255,0.12)' : 'action.hover' },
            }}
          />
        ))}
        {experimental && stable && (
          <Chip
            component="a"
            clickable
            href={buildArtifactUrls(experimental.version, brandId).github.releasePageUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => clarityService.track('cta_click', `experimental_${placement}`)}
            label={`Experimental v${experimental.version}`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ color: dark ? '#fde68a' : undefined }}
          />
        )}
      </Stack>

      {!compact && (
        <Box sx={{ mt: 2 }}>
          <NpmRow command={artifacts.npm.installCommand} copied={copied} onCopy={copyNpm} dark={dark} />
        </Box>
      )}
    </Box>
  );
};

interface NpmRowProps {
  command: string;
  copied: boolean;
  onCopy: () => void;
  dark: boolean;
}

const NpmRow: React.FC<NpmRowProps> = ({ command, copied, onCopy, dark }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      maxWidth: '100%',
      bgcolor: dark ? 'rgba(0,0,0,0.35)' : 'action.hover',
      border: '1px solid',
      borderColor: dark ? 'rgba(255,255,255,0.15)' : 'divider',
      borderRadius: 1.5,
      pl: 1.5,
      pr: 0.5,
      py: 0.5,
    }}
  >
    <Typography
      component="code"
      sx={{
        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
        fontSize: '0.85rem',
        color: dark ? 'white' : 'text.primary',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      $ {command}
    </Typography>
    <Tooltip title={copied ? 'Copied' : 'Copy install command'}>
      <IconButton size="small" onClick={onCopy} aria-label="Copy npm install command" sx={{ color: dark ? 'white' : 'text.secondary' }}>
        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  </Box>
);

export default HeroInstallCta;
